import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "../../lib/cn";
import { useAuth } from "../modals/AuthContext";
import { useAuthModal } from "../modals/AuthModalContext";

const HERO_LINES = [
  {
    headline: "Ship with two verdicts",
    detail:
      "when desktop and mobile are tested together, with both scores captured before release.",
  },
  {
    headline: "Built for speed",
    detail:
      "with AI that reads each failure and returns fix steps mapped to your stack.",
  },
  {
    headline: "Speed on the record",
    detail:
      "through saved runs, scheduled checks, and exports that keep teams aligned.",
  },
];

const heroCtaClass =
  "inline-flex items-center justify-center gap-2 rounded-full h-11 px-7 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function HeroLogo() {
  return (
    <div className="relative flex items-center justify-center py-3 sm:py-5 lg:py-0">
      <img
        src="/logo.svg"
        alt=""
        className="h-24 w-24 sm:h-28 sm:w-28 lg:h-36 lg:w-36 xl:h-40 xl:w-40"
        width={160}
        height={160}
        aria-hidden
      />
    </div>
  );
}

function HeroMonoLine({ line, index, active, onActivate }) {
  const isActive = active === index;

  return (
    <button
      type="button"
      aria-expanded={!!isActive}
      aria-label={`${line.headline}. ${line.detail}`}
      onMouseEnter={() => onActivate(index)}
      onFocus={() => onActivate(index)}
      onClick={() => onActivate(index)}
      className={cn(
        "relative z-10 m-0 w-full max-w-[18rem] cursor-default text-left",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded-sm",
      )}
    >
      <span className="block text-pretty font-mono text-sm leading-[1.6] tracking-[0.01em]">
        <span
          className={cn(
            "uppercase font-semibold tracking-[1px]",
            "transition-colors duration-500 ease-out",
            isActive ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {line.headline}
        </span>
        <span
          className={cn(
            "block text-muted-foreground font-normal normal-case",
            "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
            isActive ? "max-h-20 opacity-100" : "max-h-0 opacity-0",
          )}
        >
          {line.detail}
        </span>
      </span>
    </button>
  );
}

function HeroMonoLines({
  active,
  onActiveChange,
  className,
  resetOnLeave = false,
}) {
  return (
    <div
      className={cn("flex w-full flex-col items-start gap-4", className)}
      onMouseLeave={resetOnLeave ? () => onActiveChange(null) : undefined}
    >
      {HERO_LINES.map((line, index) => (
        <HeroMonoLine
          key={line.headline}
          line={line}
          index={index}
          active={active}
          onActivate={onActiveChange}
        />
      ))}
    </div>
  );
}

export function HomeHero() {
  const [desktopActive, setDesktopActive] = useState(null);
  const [mobileActive, setMobileActive] = useState(0);
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();

  const handleDashboardClick = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
      return;
    }

    openAuthModal("auth");
  };

  return (
    <header className="relative pt-10 sm:pt-14 lg:pt-20 pb-16 sm:pb-20 min-h-[24rem] sm:min-h-[26rem] lg:min-h-[26rem]">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_auto_minmax(0,1fr)] gap-7 lg:gap-x-14 xl:gap-x-20 lg:gap-y-8 items-center">
        <h1 className="order-2 lg:order-1 min-h-[5rem] sm:min-h-[6.25rem] lg:min-h-[7.25rem] text-[2.75rem] sm:text-[3.5rem] lg:text-[4rem] xl:text-[4.5rem] font-normal text-foreground tracking-[-0.06em] leading-[0.92]">
          <span className="block">Measure, monitor,</span>
          <span className="block">improve</span>
        </h1>

        <div className="order-1 lg:order-2 lg:px-4 xl:px-6">
          <HeroLogo />
        </div>

        <HeroMonoLines
          active={desktopActive}
          onActiveChange={setDesktopActive}
          resetOnLeave
          className="order-3 hidden lg:flex lg:pl-2 xl:pl-4"
        />
      </div>

      <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Link
          to="/test"
          className={cn(
            heroCtaClass,
            "w-full sm:w-auto bg-foreground text-background hover:bg-foreground/90",
          )}
        >
          Run Test
        </Link>
        <button
          type="button"
          onClick={handleDashboardClick}
          className={cn(
            heroCtaClass,
            "w-full sm:w-auto min-w-[11.5rem] border border-border bg-card text-foreground hover:bg-muted",
          )}
        >
          {isAuthenticated ? "Dashboard" : "Sign in for dashboard"}
        </button>
      </div>

      <HeroMonoLines
        active={mobileActive}
        onActiveChange={setMobileActive}
        className="mt-6 min-h-[7.5rem] lg:hidden"
      />
    </header>
  );
}
